// @ts-nocheck
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTeamRole } from '@/hooks/useTeamRole';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import InviteMemberDialog from './InviteMemberDialog';

interface Props {
  team: any;
}

const roleBadge: Record<string, { label: string; cls: string }> = {
  team_owner: { label: 'Owner', cls: 'bg-purple-100 text-purple-800' },
  team_manager: { label: 'Manager', cls: 'bg-blue-100 text-blue-800' },
  head_coach: { label: 'Head Coach', cls: 'bg-orange-100 text-orange-800' },
  assistant_coach: { label: 'Assistant Coach', cls: 'bg-orange-50 text-orange-700' },
  team_billing_contact: { label: 'Billing', cls: 'bg-green-100 text-green-800' },
  viewer: { label: 'Viewer', cls: 'bg-muted text-muted-foreground' },
};

export default function TeamMembersList({ team }: Props) {
  const { user } = useAuth();
  const { data: role } = useTeamRole(team.id);
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<any>(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['team-members-full', team.id],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('team_members')
        .select('*, profile:profiles(id, name, email, avatar_url)')
        .eq('team_id', team.id)
        .eq('status', 'active')
        .order('role');
      return data ?? [];
    },
  });

  const handleRemove = async () => {
    if (!confirmRemove) return;
    const { error } = await (supabase as any)
      .from('team_members')
      .update({ status: 'removed', removed_at: new Date().toISOString(), removed_by: user!.id })
      .eq('id', confirmRemove.id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['team-members-full', team.id] });
    }
    setConfirmRemove(null);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Members</CardTitle>
        {role?.isAdmin && (
          <Button size="sm" onClick={() => setInviteOpen(true)}>Invite member</Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Avatar</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Primary billing</TableHead>
                <TableHead>Joined</TableHead>
                {role?.isOwner && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m: any) => {
                const rb = roleBadge[m.role] ?? { label: m.role, cls: 'bg-muted' };
                const isSelf = m.user_id === user?.id;
                return (
                  <TableRow key={m.id}>
                    <TableCell>
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={m.profile?.avatar_url} />
                        <AvatarFallback>{m.profile?.name?.[0] ?? '?'}</AvatarFallback>
                      </Avatar>
                    </TableCell>
                    <TableCell>{m.profile?.name ?? '—'}</TableCell>
                    <TableCell>{m.profile?.email ?? '—'}</TableCell>
                    <TableCell>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${rb.cls}`}>{rb.label}</span>
                    </TableCell>
                    <TableCell>{m.is_primary_billing ? <Badge>Yes</Badge> : '—'}</TableCell>
                    <TableCell>{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}</TableCell>
                    {role?.isOwner && (
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" disabled={isSelf} onClick={() => toast.info('Role change coming soon')}>
                            Change role
                          </Button>
                          <Button size="sm" variant="destructive" disabled={isSelf} onClick={() => setConfirmRemove(m)}>
                            Remove
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        teamId={team.id}
        teamName={team.canonical_name ?? team.display_name ?? 'this team'}
        callerIsOwner={!!role?.isOwner}
        onMemberInvited={() => {
          queryClient.invalidateQueries({ queryKey: ['team-members-full', team.id] });
          queryClient.invalidateQueries({ queryKey: ['team-members', team.id] });
          setInviteOpen(false);
        }}
      />

      <Dialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to remove {confirmRemove?.profile?.name ?? 'this member'}?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRemove}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
